import { useEffect, useMemo, useRef, useState } from 'react'
import { ExternalLink, Loader2, Monitor, Smartphone } from 'lucide-react'
import { cleanTemplateId, getTemplate } from '../lib/inviteTemplates'

/**
 * The Design step's live preview: the real invitation page at /templates/:id
 * in one iframe, wearing the host's template, colours, font, words and photo
 * (all through the query string), shown in a phone or scaled down to a
 * desktop. CreateEvent keeps it in a sticky column beside the designer so
 * every pick shows up without scrolling back. Only one iframe is ever mounted
 * — each boots a full copy of the app.
 */

export type PreviewWords = {
  title?: string
  honoree?: string
  date?: string
  time?: string
  location?: string
}

type Props = {
  template?: string
  accent?: string
  background?: string
  font?: string
  /** The invitation's language. */
  lang?: string
  appLang?: string
  /** The host's own photo. Painted templates carry none, so it is left off for them. */
  cover?: string | null
  words?: PreviewWords
  /** Told which size is showing, so the page can give the desktop view the room it needs. */
  onDevice?: (device: 'phone' | 'desktop') => void
}

const DESKTOP_W = 1280
const DESKTOP_H = 800
const WORD_LIMIT = 160

const InvitePreviewFrame = ({ template, accent, background, font, lang = 'en', appLang = 'en', cover, words, onDevice }: Props) => {
  const t = (en: string, ka: string) => (appLang === 'ka' ? ka : en)
  const id = cleanTemplateId(template)
  const tpl = getTemplate(id)
  const [device, setDevice] = useState<'phone' | 'desktop'>('phone')
  const [loading, setLoading] = useState(true)
  const boxRef = useRef<HTMLDivElement>(null)
  // Seeded with the column's usual width so the desktop frame renders on the
  // first paint instead of waiting for the observer (or forever without one).
  const [box, setBox] = useState({ w: 320 })

  // Also on mount: leaving the step unmounts the frame back to phone, and the
  // page's column has to come back with it.
  useEffect(() => {
    onDevice?.(device)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device])

  useEffect(() => {
    const el = boxRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(([entry]) => setBox({ w: entry.contentRect.width }))
    ro.observe(el)
    return () => ro.disconnect()
  }, [device])

  const src = useMemo(() => {
    const q = new URLSearchParams({ noIntro: '1', lang: lang === 'ka' ? 'ka' : 'en' })
    if (accent) q.set('accent', accent.replace(/^#/, ''))
    if (background) q.set('bg', background.replace(/^#/, ''))
    if (font) q.set('font', font)
    if (cover && !tpl.painted) q.set('cover', cover)
    Object.entries(words || {}).forEach(([k, v]) => {
      const text = String(v || '').trim()
      if (text) q.set(k, text.slice(0, WORD_LIMIT))
    })
    return `/templates/${id}?${q.toString()}`
  }, [id, tpl.painted, lang, accent, background, font, cover, words])

  useEffect(() => {
    setLoading(true)
  }, [src, device])

  const scale = box.w ? box.w / DESKTOP_W : 0
  const title = appLang === 'ka' ? tpl.label.ka : tpl.label.en

  const deviceBtn = (on: boolean) =>
    `flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
      on ? 'bg-[#ff4d8d] text-white shadow-sm' : 'text-[#4a3d66] hover:text-[#ff4d8d] dark:text-gray-400'
    }`

  const spinner = loading && (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-[#1e1438]/70">
      <Loader2 className="h-5 w-5 animate-spin text-[#ff4d8d]" />
    </div>
  )

  return (
    <div className="space-y-3" data-testid="invite-preview">
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
            {t('Invitation preview', 'მოწვევის გადახედვა')}
          </p>
          <p className="truncate text-xs text-[#4a3d66] dark:text-gray-400">{title}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <div
            className="mr-1 flex items-center rounded-lg border border-[#1b1033]/10 p-0.5 dark:border-white/10"
            role="group"
            aria-label={t('Preview size', 'გადახედვის ზომა')}
          >
            <button
              type="button"
              onClick={() => setDevice('phone')}
              aria-pressed={device === 'phone'}
              aria-label={t('Phone preview', 'მობილურის ხედი')}
              title={t('Phone', 'მობილური')}
              className={deviceBtn(device === 'phone')}
              data-testid="invite-preview-phone"
            >
              <Smartphone className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setDevice('desktop')}
              aria-pressed={device === 'desktop'}
              aria-label={t('Desktop preview', 'დესკტოპის ხედი')}
              title={t('Desktop', 'დესკტოპი')}
              className={deviceBtn(device === 'desktop')}
              data-testid="invite-preview-desktop"
            >
              <Monitor className="h-4 w-4" />
            </button>
          </div>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            title={t('Open full size', 'სრული ზომით')}
            aria-label={t('Open the preview in a new tab', 'გადახედვის გახსნა ახალ ფანჯარაში')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#4a3d66] transition-colors hover:text-[#ff4d8d] dark:text-gray-400"
            data-testid="invite-template-open"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      {device === 'phone' ? (
        <div className="mx-auto w-full max-w-[340px] overflow-hidden rounded-[2.25rem] border-[10px] border-[#1b1033] bg-[#1b1033] shadow-2xl shadow-[#1b1033]/25 dark:border-black dark:bg-black">
          <div className="relative h-[620px] overflow-hidden rounded-[1.6rem] bg-white dark:bg-[#1e1438] lg:h-[calc(100dvh-var(--preview-top,6rem)-5.5rem)] lg:max-h-[760px] lg:min-h-[420px]">
            {spinner}
            <iframe
              key={`phone-${src}`}
              src={src}
              title={title}
              onLoad={() => setLoading(false)}
              // A read-only render of our own route: no forms to submit, no
              // navigation, nothing for the guest flows to reach.
              sandbox="allow-scripts allow-same-origin"
              className="h-full w-full border-0"
              data-testid="invite-preview-frame"
            />
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#1b1033]/12 shadow-2xl shadow-[#1b1033]/15 dark:border-white/10">
          <div className="flex items-center gap-1.5 border-b border-[#1b1033]/10 bg-[#1b1033]/[0.04] px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          </div>
          {/* 16:10, the shape of a real desktop viewport — a frame as tall as
              the sticky column would lay the invitation out for a screen
              nobody has. */}
          <div ref={boxRef} className="relative aspect-[16/10] w-full overflow-hidden bg-white dark:bg-[#1e1438]">
            {spinner}
            {scale > 0 && (
              <iframe
                key={`desktop-${src}`}
                src={src}
                title={title}
                onLoad={() => setLoading(false)}
                sandbox="allow-scripts allow-same-origin"
                scrolling="no"
                style={{
                  width: DESKTOP_W,
                  height: DESKTOP_H,
                  border: 0,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                }}
                data-testid="invite-preview-frame"
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default InvitePreviewFrame
